import { createRouter, createWebHistory, type RouteLocationNormalized, type NavigationGuardNext } from 'vue-router';
import { mfhRamEmbedPropsForExternalConstructionPlansRoute } from './mfhRamEmbedRouteExternalProps';
import { getClient } from '@/api/api-client';
import type { MFHExpandOpts } from '@condo/domain';
import { isMfhAcquisitionBlockedForThisPartner } from '@condo/domain';

const checkMfhAcquisitionAccess = async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
    try {
        const mfhId = to.params.mfhId;

        const expand: MFHExpandOpts = {
            select: ['mfhId'],
            expand: {
                candidateMfhPortfolios: {
                    select: ['mfhPortfolioId', 'assetPartnerId'],
                    expand: {
                        mfhCandidatePortfolioRelations: { select: ['mfhCandidatePortfolioRelationId', 'mfhId', 'companyId', 'blockedByPortfolioId'] },
                    },
                },
            },
        };
        const response = await getClient('externalApi').getMfh({ mfhId: Number(mfhId) }, { expand: expand as any });

        if (response?.mfh && isMfhAcquisitionBlockedForThisPartner(response.mfh)) {
            return next({ name: 'MFHDetails', params: { mfhId } });
        }

        return next();
    } catch (error) {
        return next({ name: 'MFHDetails', params: { mfhId: to.params.mfhId } });
    }
};

const getRouter = () =>
    createRouter({
        history: createWebHistory(),
        linkActiveClass: 'open active',
        scrollBehavior(to, from) {
            if (to.hash) {
                return new Promise(resolve => {
                    setTimeout(() => {
                        resolve({
                            el: to.hash,
                            top: 100,
                            behavior: 'smooth',
                        });
                    }, 1000);
                });
            }

            if (from.name && (history.state as { preserveMainScroll?: boolean } | null)?.preserveMainScroll) {
                return false;
            }

            return { top: 0 };
        },
        routes: [
            {
                path: '/',
                name: 'Home',
                component: () => import('@/external/components/layout/DefaultLayout.vue'),
                children: [
                    {
                        name: 'AssetsList',
                        path: '/',
                        meta: {
                            title: 'Assets List',
                            icon: 'list-dropdown',
                        },
                        component: () => import('@/external/views/ExternalAssetList.vue'),
                    },
                    {
                        name: 'PortfoliosList',
                        path: 'portfolios',
                        meta: {
                            title: 'Portfolios',
                            icon: 'list-dropdown',
                        },
                        component: () => import('@/external/views/ExternalPortfoliosList.vue'),
                    },
                    {
                        name: 'InvoicesList',
                        path: 'invoices',
                        meta: {
                            title: 'Invoices List',
                            icon: 'file-invoice-dollar',
                        },
                        component: () => import('@/external/views/ExternalInvoicesList.vue'),
                    },
                    {
                        name: 'OneInvoice',
                        path: 'invoices/:invoiceId/details',
                        props: true,
                        meta: {
                            title: 'Invoice Details',
                            icon: 'file-invoice-dollar',
                        },
                        component: () => import('@/external/components/invoice/ExternalInvoiceDetails.vue'),
                    },
                    {
                        name: 'Calendar',
                        path: 'calendar',
                        meta: {
                            title: 'Calendar',
                            icon: 'calendar',
                        },
                        component: () => import('@/external/views/ExternalCalendar.vue'),
                    },
                    {
                        name: 'DataRoom',
                        path: 'data-room',
                        meta: {
                            title: 'Data Room',
                            icon: 'document',
                        },
                        component: () => import('@/external/views/ExternalDataRoom.vue'),
                    },
                    {
                        name: 'Reporting',
                        path: 'reporting',
                        component: () => import('@/external/views/ExternalMfhReporting.vue'),
                        props: true,
                    },
                    {
                        name: 'SignOffEnvelopesList',
                        path: 'sign-off-envelopes',
                        meta: {
                            title: 'Approval Envelopes List',
                            icon: 'signature',
                        },
                        component: () => import('@/external/views/ExternalSignOffEnvelopesList.vue'),
                    },
                    {
                        name: 'SignOffEnvelopeDetails',
                        path: 'sign-off-envelopes/:signOffEnvelopeId',
                        props: true,
                        meta: {
                            title: 'Approval',
                            icon: 'signature',
                        },
                        component: () => import('@/external/views/ExternalSignOffEnvelopeDetails.vue'),
                    },
                    {
                        name: 'Profile',
                        path: 'profile',
                        meta: {
                            title: 'Profile',
                            icon: 'user',
                        },
                        component: () => import('@/external/views/ExternalProfilePage.vue'),
                    },
                ],
            },
            {
                name: 'OnePortfolio',
                path: '/portfolios/:mfhPortfolioId',
                props: true,
                component: () => import('@/external/views/ExternalOnePortfolio.vue'),
                redirect: params => {
                    return `${params.fullPath}/sheet-embedding`;
                },
                meta: { title: 'Portfolio Details', icon: 'briefcase' },
                children: [
                    {
                        name: 'PortfolioSheetEmbedding',
                        path: 'sheet-embedding/:mfhSheetEmbeddingId?',
                        component: () => import('@/external/views/ExternalPortfolioSheetEmbedding.vue'),
                        props: route => ({
                            mfhPortfolioId: route.params.mfhPortfolioId,
                            ...(route.params.mfhSheetEmbeddingId != null && route.params.mfhSheetEmbeddingId !== ''
                                ? { mfhSheetEmbeddingId: route.params.mfhSheetEmbeddingId }
                                : {}),
                        }),
                    },
                ],
            },
            {
                name: 'OneMFH',
                path: '/mfhs/:mfhId',
                props: true,
                component: () => import('@/external/views/ExternalOneMfh.vue'),
                // Object form on purpose: appending to fullPath puts the segment inside a
                // query/hash so the path never changes → infinite redirect loop.
                redirect: to => ({ path: `${to.path}/details`, query: to.query, hash: to.hash }),
                meta: {
                    title: 'Asset Details',
                    icon: 'circle-info',
                },
                children: [
                    {
                        name: 'MFHDetails',
                        path: 'details',
                        component: () => import('@/external/components/mfh/ExternalMfhDetails.vue'),
                        props: true,
                    },
                    {
                        name: 'one.mfh.summary',
                        path: 'summary',
                        component: () => import('@/external/views/ExternalMfhSummary.vue'),
                        props: true,
                    },
                    {
                        name: 'MFHDocuments',
                        path: 'documents',
                        component: () => import('@/external/components/mfh/ExternalMfhDocuments.vue'),
                        props: true,
                    },
                    {
                        name: 'MFHInvestmentCases',
                        path: 'investment-cases',
                        component: () => import('@/external/components/mfh/ExternalMfhInvestmentCases.vue'),
                        props: true,
                    },
                    {
                        name: 'MFHInvestmentCase',
                        path: 'investment-cases/:mfhInvestmentCaseId',
                        component: () => import('@/external/components/mfh/ExternalMfhInvestmentCase.vue'),
                        props: true,
                    },
                    // routes that are restricted if mfh-acquisition is blocked for partner
                    ...[
                        {
                            name: 'MFHEstates',
                            path: 'estates',
                            component: () => import('@/external/components/mfh/ExternalMfhEstates.vue'),
                            props: true,
                        },
                        {
                            name: 'MFHRentRollReporting',
                            path: 'rent-roll-reporting',
                            component: () => import('@/external/components/mfh/ExternalMfhRentRollReporting.vue'),
                            props: true,
                        },
                        {
                            name: 'MFHReporting',
                            path: 'reporting',
                            component: () => import('@/external/views/ExternalMfhReporting.vue'),
                            props: true,
                        },
                        {
                            name: 'MFHContacts',
                            path: 'contacts',
                            component: () => import('@/external/components/mfh/ExternalMfhContacts.vue'),
                            props: true,
                        },
                        {
                            name: 'MFHChecklists',
                            path: 'checklists',
                            component: () => import('@/external/components/mfh/ExternalMfhChecklists.vue'),
                            props: true,
                        },
                        {
                            name: 'MFHMedias',
                            path: 'medias',
                            component: () => import('@/external/components/mfh/ExternalMfhMedias.vue'),
                            props: true,
                        },
                        {
                            name: 'MFHAssetUnits',
                            path: 'units',
                            component: () => import('@/external/components/mfh/ExternalMfhUnits.vue'),
                            props: true,
                        },
                        {
                            name: 'MFHSignOffEnvelopes',
                            path: 'sign-off-envelopes',
                            component: () => import('@/external/views/ExternalSignOffEnvelopesList.vue'),
                            props: true,
                        },
                        {
                            name: 'MFHConstructionPlans',
                            path: 'construction-plans',
                            components: {
                                default: () => import('@/components/ram/RamEmbed.vue'),
                            },
                            props: {
                                default: mfhRamEmbedPropsForExternalConstructionPlansRoute,
                            },
                        },
                        {
                            name: 'OneEstate',
                            path: 'estates/:estateId',
                            props: true,
                            component: () => import('@/external/views/ExternalOneEstate.vue'),
                            redirect: to => `${to.fullPath}/media`,
                            meta: {
                                title: 'Estate Overview',
                                icon: 'building',
                            },
                            children: [
                                {
                                    name: 'EstateRenovationOverview',
                                    path: 'renovation-overview',
                                    component: () => import('@/external/components/mfh/ExternalEstateRenovationOverview.vue'),
                                    props: true,
                                },
                                {
                                    name: 'EstateMedia',
                                    path: 'media',
                                    component: () => import('@/external/components/mfh/ExternalEstateMedia.vue'),
                                    props: true,
                                },
                            ],
                        },
                    ].map(route => ({ ...route, beforeEnter: checkMfhAcquisitionAccess })),
                ],
            },
            {
                path: '/accounting/invoices/:invoiceId',
                redirect: to => `/invoices/${to.params.invoiceId}/details`,
            },
            {
                path: '/:catchAll(.*)*',
                component: () => import('@/external/views/ExternalNotFound.vue'),
            },
        ],
    });

export const router = getRouter();
